/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useContext,
  useEffect,
  ReactNode,
  PropsWithChildren,
} from 'react';

import ReactInfiniteScroll, { Props } from 'react-infinite-scroll-component';

import { useIsInsideContainer, useShadowRootElement } from '../hooks';

import { useStyles } from './InfiniteScroll.styles';
import { InfiniteScrollContext } from './InfiniteScrollProvider';

interface InfiniteScrollProps extends Props {
  scrollId?: string;
  scrollableDialog?: boolean;
  withOverflow?: boolean;
  isLoading?: boolean;
  hasMore: boolean;
  next: () => void;
}

/**
 * Пользовательский компонент InfiniteScroll, который расширяет `react-infinite-scroll-component`.
 * Он позволяет при необходимости применять стили для переполнения и использует контекст для определения целевого элемента прокрутки.
 *
 * @param {Object} props - Свойства, которые можно передать в компонент InfiniteScroll.
 * @param {boolean} [props.withOverflow=false] - Если true, применяет стили для переполнения к компоненту.
 * @param {React.ReactNode} children - Содержимое, которое будет отображено внутри компонента InfiniteScroll.
 * @param {string} [props.scrollId] - id элемента, который будет использоваться как контейнер прокрутки.
 * @returns {JSX.Element} JSX-элемент, представляющий компонент InfiniteScroll.
 */

const SCROLLABLE_DIALOG_ID = 'scrollable-dialog';

export const InfiniteScroll = ({
  children,
  scrollableDialog = false,
  scrollId,
  ...props
}: PropsWithChildren<InfiniteScrollProps>) => {
  const { classes } = useStyles();
  const { withOverflow } = props;
  const scrollContext = useContext(InfiniteScrollContext);
  const scrollTarget = scrollableDialog ? SCROLLABLE_DIALOG_ID : scrollContext;
  const isInsideContainer = useIsInsideContainer();

  const scrollWidgetId = useShadowRootElement(scrollId);

  let finalScrollTarget: string | HTMLElement | null;

  if (!isInsideContainer) {
    finalScrollTarget = scrollId ?? scrollTarget;
  } else {
    finalScrollTarget = scrollWidgetId;
  }

  useEffect(() => {
    if (!props.hasMore || props.isLoading) {
      return;
    }

    const container =
      typeof finalScrollTarget === 'string'
        ? document.getElementById(finalScrollTarget)
        : finalScrollTarget;

    if (!container) {
      return;
    }

    const hasScrollbar = container.scrollHeight > container.clientHeight;

    if (!hasScrollbar) {
      props.next();
    }
  }, [props.dataLength, props.hasMore, props.isLoading]);

  return finalScrollTarget ? (
    <ReactInfiniteScroll
      {...props}
      {...(withOverflow && {
        className: classes.infiniteScroll,
      })}
      scrollableTarget={finalScrollTarget as unknown as ReactNode}
    >
      {children}
    </ReactInfiniteScroll>
  ) : (
    <ReactInfiniteScroll
      {...props}
      {...(withOverflow && {
        className: classes.infiniteScroll,
      })}
    >
      {children}
    </ReactInfiniteScroll>
  );
};
